import { useMemo } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Accordion, DropdownField, Heading, RadioCardField, Text } from '@frontend/design-system';
import { StatusBadge } from '../../../status-badge';
import { AvailabilityFormRow } from '../../availability-form/atoms/availability-form-row';
import { PreviewNotification } from '../../availability-form/atoms/preview-notification';
import { RadioCardFieldRow } from '../../availability-form/atoms/radio-card-field-row';
import {
  BookingCadenceOptions,
  bookingCadenceOptions,
  GetAvailabilityRuleFormFieldProps,
  patientBookingOptions,
} from '../../availability-form/types';
import {
  convertTimeToMinutes,
  getCadencePreviewDescriptionForRange,
  getSelectedServiceDuration,
} from '../../helpers/booking-rules-form-helpers';

type LegacyBookingRulesFormProps = {
  locationId: string | undefined;
  getFieldProps: GetAvailabilityRuleFormFieldProps;
};

export const LegacyBookingRulesForm = ({ locationId, getFieldProps }: LegacyBookingRulesFormProps) => {
  const { t } = useTranslation('schedule-settings');
  const bookingCadenceFieldProps = getFieldProps('bookingCadence');
  const patientBookingFieldProps = getFieldProps('patientBooking');
  const servicesFieldProps = getFieldProps('services');
  const startTime = getFieldProps('startTime') ?? '';
  const endTime = getFieldProps('endTime') ?? '';

  const serviceTypesQuery = SchedulerV3.Helpers.useGetServiceTypesV3Data({
    selectedLocationId: locationId || '',
  });

  const selectedServiceDuration = useMemo(() => {
    return getSelectedServiceDuration({
      appointmentTypeGroups: serviceTypesQuery.data?.appointmentTypeGroups ?? [],
      selectedServiceIds: servicesFieldProps.value ?? [],
    });
  }, [serviceTypesQuery.data?.appointmentTypeGroups, servicesFieldProps]);

  const previewNotificationDescription = useMemo(() => {
    const startMinutes = convertTimeToMinutes(startTime?.value);
    const endMinutes = convertTimeToMinutes(endTime?.value);
    const cadenceId = bookingCadenceFieldProps.value as BookingCadenceOptions | undefined;

    if (
      !selectedServiceDuration ||
      !cadenceId ||
      startMinutes === null ||
      endMinutes === null ||
      endMinutes <= startMinutes
    ) {
      return null;
    }

    return getCadencePreviewDescriptionForRange({
      cadenceId,
      startMinutes,
      endMinutes,
      serviceDuration: selectedServiceDuration,
    });
  }, [bookingCadenceFieldProps.value, startTime, endTime, selectedServiceDuration]);

  const headerSubtitle = useMemo(() => {
    const cadenceLabel = bookingCadenceOptions.find((o) => o.id === bookingCadenceFieldProps.value)?.displayName;
    const patientBookingLabel = patientBookingOptions.find((o) => o.id === patientBookingFieldProps.value)?.id;
    const statusBadgeVariant =
      patientBookingFieldProps.value === 'REQUEST' ? 'request-only-booking' : 'staff-only-booking';

    return (
      <div css={subHeaderStyles}>
        <Text>{cadenceLabel}</Text>
        {patientBookingLabel ? <div css={separatorStyles} /> : null}
        <StatusBadge variant={statusBadgeVariant} />
      </div>
    );
  }, [bookingCadenceFieldProps.value, patientBookingFieldProps.value]);

  return (
    <Accordion.Item value='booking-rules'>
      <Accordion.Header>
        <div css={accordionHeaderStyles}>
          <Heading level={3}>{t('Booking Rules')}</Heading>
          {headerSubtitle}
        </div>
      </Accordion.Header>
      <Accordion.Body>
        <AvailabilityFormRow>
          <DropdownField label={t('Booking Cadence')} {...bookingCadenceFieldProps} hasNonFloatingLabel>
            {bookingCadenceOptions?.map((item) => (
              <DropdownField.Option key={item.id} value={item.id}>
                {item.displayName}
              </DropdownField.Option>
            ))}
          </DropdownField>
        </AvailabilityFormRow>
        {previewNotificationDescription && (
          <AvailabilityFormRow>
            <PreviewNotification title={t('Cadence preview:')} description={previewNotificationDescription} />
          </AvailabilityFormRow>
        )}
        <AvailabilityFormRow>
          <RadioCardFieldRow label={t('Patient Booking')} {...getFieldProps('patientBooking')} hasNonFloatingLabel>
            {patientBookingOptions?.map((item) => (
              <RadioCardField.Option key={item.id} value={item.id} title={item.title} description={item.description} />
            ))}
          </RadioCardFieldRow>
        </AvailabilityFormRow>
      </Accordion.Body>
    </Accordion.Item>
  );
};

const accordionHeaderStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const subHeaderStyles = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-sm);
`;

const separatorStyles = css`
  width: 1px;
  height: 100%;
  background-color: var(--fabric-color-neutral-alpha-100-alpha-75);
`;
